<template>
  <div class="tw:mx-auto tw:bg-white tw:rounded-lg tw:w-full tw:p-4" role="application" aria-label="Date picker">
    <div class="tw:flex tw:items-center tw:justify-between tw:mb-6">
      <button
        v-if="canGoToPreviousMonth"
        @click="previousMonth"
        aria-label="Previous month"
        class="tw:p-2 tw:hover:bg-gray-100 tw:rounded-lg tw:transition-colors tw:-ml-2"
      >
        <svg width="7" height="12" viewBox="0 0 7 12" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
          <path d="M6 1L1 6" stroke="#0A1121" stroke-width="2" stroke-linecap="round" />
          <path d="M6 11L1 6" stroke="#0A1121" stroke-width="2" stroke-linecap="round" />
        </svg>
      </button>

      <div class="tw:flex-1 tw:grid tw:gap-8" :class="showTwoMonths ? 'tw:grid-cols-2' : 'tw:grid-cols-1'">
        <h2 class="tw:text-gray-900 tw:text-center tw:font-bold" :class="showTwoMonths ? 'tw:mr-[28px]' : ''">
          {{ `${$t(monthNames[getMonthYearDisplay(0).month])} ${getMonthYearDisplay(0).year}` }}
        </h2>
        <h2
          v-if="showTwoMonths"
          class="tw:text-gray-900 tw:text-center tw:font-bold tw:ml-[28px]"
          :id="`month-${getMonthYearDisplay(1).month}-${getMonthYearDisplay(1).year}`"
          aria-live="polite"
        >
          {{ `${$t(monthNames[getMonthYearDisplay(1).month])} ${getMonthYearDisplay(1).year}` }}
        </h2>
      </div>

      <button
        v-if="canGoToNextMonth"
        @click="nextMonth"
        aria-label="Next month"
        class="tw:p-2 tw:hover:bg-gray-100 tw:rounded-lg tw:transition-colors tw:-mr-2"
      >
        <svg width="7" height="12" viewBox="0 0 7 12" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
          <path d="M1 11L6 6" stroke="#0A1121" stroke-width="2" stroke-linecap="round" />
          <path d="M1 1L6 6" stroke="#0A1121" stroke-width="2" stroke-linecap="round" />
        </svg>
      </button>
    </div>

    <div class="tw:grid tw:gap-8" :class="showTwoMonths ? 'tw:grid-cols-2' : 'tw:grid-cols-1'">
      <!-- First Month -->
      <div role="grid" :aria-labelledby="`month-${getMonthYearDisplay(0).month}-${getMonthYearDisplay(0).year}`">
        <div
          class="tw:grid tw:grid-cols-7 tw:gap-2 tw:mb-2 tw:bg-gray-200 tw:py-2 tw:mx-0 tw:px-6 tw:lg:px-12"
          role="row"
        >
          <div
            v-for="day in daysOfWeek"
            :key="'dow-1-' + day.value"
            role="columnheader"
            class="tw:text-center tw:text-xs tw:text-gray-800"
            :class="{ 'tw:font-bold': day.value === 'Sat' || day.value === 'Sun' }"
            :aria-label="day.value"
          >
            {{ $t(day.label) }}
          </div>
        </div>

        <div class="tw:grid tw:grid-cols-7 tw:px-4 tw:lg:px-10" role="rowgroup">
          <div
            v-for="blank in getFirstDayOfMonth(0)"
            :key="'blank-1-' + blank"
            class="tw:w-full tw:pt-[100%] tw:relative"
            role="gridcell"
            aria-hidden="true"
          />

          <div
            v-for="day in getDaysInMonth(0)"
            :key="'day-1-' + day"
            class="tw:w-full tw:pt-[100%] tw:relative"
            role="gridcell"
          >
            <UiTooltip
              v-if="shouldShowTooltip(0, day)"
              theme="material"
              tooltip-position="bottom"
              :label="$t('rc_list_bookings_commission', { value: getTooltipCommission(0, day) })"
            >
              <button
                @mousedown="handleMouseDown(0, day)"
                @mouseenter="handleMouseEnter(0, day)"
                @mouseup="handleMouseUp"
                @keydown.enter="handleKeyboardSelect(0, day)"
                @keydown.space.prevent="handleKeyboardSelect(0, day)"
                :disabled="!isDateSelectable(0, day)"
                :aria-disabled="!isDateSelectable(0, day)"
                :data-testid="getDayTestId(0, day)"
                :aria-label="getDateAriaLabel(0, day)"
                :aria-pressed="isSelected(0, day)"
                :aria-current="isToday(0, day) ? 'date' : undefined"
                class="tw:absolute tw:inset-0 tw:flex tw:items-center tw:justify-center tw:rounded-full tw:text-sm"
                :class="getDayClasses(0, day)"
              >
                {{ day }}
              </button>
            </UiTooltip>
            <button
              v-else
              @mousedown="handleMouseDown(0, day)"
              @mouseenter="handleMouseEnter(0, day)"
              @mouseup="handleMouseUp"
              @keydown.enter="handleKeyboardSelect(0, day)"
              @keydown.space.prevent="handleKeyboardSelect(0, day)"
              :disabled="!isDateSelectable(0, day)"
              :aria-disabled="!isDateSelectable(0, day)"
              :data-testid="getDayTestId(0, day)"
              :aria-label="getDateAriaLabel(0, day)"
              :aria-pressed="isSelected(0, day)"
              :aria-current="isToday(0, day) ? 'date' : undefined"
              class="tw:absolute tw:inset-0 tw:flex tw:items-center tw:justify-center tw:rounded-full tw:text-sm"
              :class="getDayClasses(0, day)"
            >
              {{ day }}
            </button>
          </div>
        </div>
      </div>

      <!-- Second Month (conditional) -->
      <div
        v-if="showTwoMonths"
        role="grid"
        :aria-labelledby="`month-${getMonthYearDisplay(1).month}-${getMonthYearDisplay(1).year}`"
      >
        <div
          class="tw:grid tw:grid-cols-7 tw:gap-2 tw:mb-2 tw:bg-gray-200 tw:py-2 tw:mx-0 tw:px-6 tw:lg:px-12"
          role="row"
        >
          <div
            v-for="day in daysOfWeek"
            :key="'dow-2-' + day.value"
            role="columnheader"
            class="tw:text-center tw:text-xs tw:text-gray-800"
            :class="{ 'tw:font-bold': day.value === 'Sat' || day.value === 'Sun' }"
            :aria-label="day.value"
          >
            {{ $t(day.label) }}
          </div>
        </div>

        <div class="tw:grid tw:grid-cols-7 tw:px-4 tw:lg:px-10" role="rowgroup">
          <div
            v-for="blank in getFirstDayOfMonth(1)"
            :key="'blank-2-' + blank"
            class="tw:w-full tw:pt-[100%] tw:relative"
            role="gridcell"
            aria-hidden="true"
          />

          <div
            v-for="day in getDaysInMonth(1)"
            :key="'day-2-' + day"
            class="tw:w-full tw:pt-[100%] tw:relative"
            role="gridcell"
          >
            <UiTooltip
              v-if="shouldShowTooltip(1, day)"
              theme="material"
              tooltip-position="bottom"
              :label="$t('rc_list_bookings_commission', { value: getTooltipCommission(1, day) })"
            >
              <button
                @mousedown="handleMouseDown(1, day)"
                @mouseenter="handleMouseEnter(1, day)"
                @mouseup="handleMouseUp"
                @keydown.enter="handleKeyboardSelect(1, day)"
                @keydown.space.prevent="handleKeyboardSelect(1, day)"
                :disabled="!isDateSelectable(1, day)"
                :aria-disabled="!isDateSelectable(1, day)"
                :data-testid="getDayTestId(1, day)"
                :aria-label="getDateAriaLabel(1, day)"
                :aria-pressed="isSelected(1, day)"
                :aria-current="isToday(1, day) ? 'date' : undefined"
                class="tw:absolute tw:inset-0 tw:flex tw:items-center tw:justify-center tw:rounded-full tw:text-sm"
                :class="getDayClasses(1, day)"
              >
                {{ day }}
              </button>
            </UiTooltip>
            <button
              v-else
              @mousedown="handleMouseDown(1, day)"
              @mouseenter="handleMouseEnter(1, day)"
              @mouseup="handleMouseUp"
              @keydown.enter="handleKeyboardSelect(1, day)"
              @keydown.space.prevent="handleKeyboardSelect(1, day)"
              :disabled="!isDateSelectable(1, day)"
              :aria-disabled="!isDateSelectable(1, day)"
              :data-testid="getDayTestId(1, day)"
              :aria-label="getDateAriaLabel(1, day)"
              :aria-pressed="isSelected(1, day)"
              :aria-current="isToday(1, day) ? 'date' : undefined"
              class="tw:absolute tw:inset-0 tw:flex tw:items-center tw:justify-center tw:rounded-full tw:text-sm"
              :class="getDayClasses(1, day)"
            >
              {{ day }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, watch } from 'vue'

import UiTooltip from '@/components/dashboard/ui/UiTooltip.vue'

import { useSmartBoost } from '@/components/rateConnect/smartBoost/useSmartBoost.js'
import { useActiveBoosts } from '../queries'
import { selectedAccommodationId } from '@/layouts/queries'
import { useSessionValues } from '@/composables/useSessionValues'

const props = defineProps({
  showTwoMonths: {
    type: Boolean,
    default: true,
  },
  isEditing: {
    type: Boolean,
    default: false,
  },
  hasReachedLimit: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits(['selectionComplete', 'switchToCreationMode'])

const {
  hasDate,
  toggleDate,
  setRange,
  hasActiveBoostDate,
  getSelectedDatesArray,
  getBoostTimestamps,
  calendarCurrentMonth,
  isDragging,
  dragStart,
  dragEnd,
  hasMovedToAnotherDate,
  setDragging,
  setDragStart,
  setDragEnd,
  setHasMovedToAnotherDate,
  resetDragState,
} = useSmartBoost()

const currentDate = calendarCurrentMonth

const { language } = useSessionValues()
const { activeBoosts } = useActiveBoosts(selectedAccommodationId)

const daysOfWeek = [
  { value: 'Mon', label: 'smart_boost_calendar_monday' },
  { value: 'Tue', label: 'smart_boost_calendar_tuesday' },
  { value: 'Wed', label: 'smart_boost_calendar_wednesday' },
  { value: 'Thu', label: 'smart_boost_calendar_thursday' },
  { value: 'Fri', label: 'smart_boost_calendar_friday' },
  { value: 'Sat', label: 'smart_boost_calendar_saturday' },
  { value: 'Sun', label: 'smart_boost_calendar_sunday' },
]
const monthNames = [
  'month_1',
  'month_2',
  'month_3',
  'month_4',
  'month_5',
  'month_6',
  'month_7',
  'month_8',
  'month_9',
  'month_10',
  'month_11',
  'month_12',
]

watch(
  () => props.showTwoMonths,
  (newValue, oldValue) => {
    if (oldValue === true && newValue === false) {
      if (getSelectedDatesArray.value.length > 0) {
        const firstTimestamp = Math.min(...getSelectedDatesArray.value)
        const firstDate = new Date(firstTimestamp)
        firstDate.setDate(1)
        currentDate.value = firstDate
      }
    }
  }
)

const shouldShowTooltip = (offset, day) => {
  const timestamp = getDateTimestamp(offset, day)

  return isSelected(offset, day) && hasActiveBoostDate(timestamp)
}

const getCommissionForDate = timestamp => {
  if (!activeBoosts.value || Object.keys(activeBoosts.value).length === 0) {
    return null
  }

  for (const [commissionLevel, boosts] of Object.entries(activeBoosts.value)) {
    for (const boost of boosts) {
      const boostTimestamps = getBoostTimestamps(boost)
      if (boostTimestamps.includes(timestamp)) {
        return commissionLevel
      }
    }
  }
  return null
}

const getTooltipCommission = (offset, day) => {
  const timestamp = getDateTimestamp(offset, day)
  const commission = getCommissionForDate(timestamp)

  return new Intl.NumberFormat(language.value, {
    style: 'percent',
  }).format(commission / 100)
}

const canGoToPreviousMonth = computed(() => {
  const firstMonthDate = new Date(currentDate.value)
  firstMonthDate.setDate(1)
  firstMonthDate.setHours(0, 0, 0, 0)

  const today = new Date()
  today.setDate(1)
  today.setHours(0, 0, 0, 0)

  return firstMonthDate > today
})

const canGoToNextMonth = computed(() => {
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const maxDate = new Date(today)
  maxDate.setDate(maxDate.getDate() + 364)

  const monthsToCheck = props.showTwoMonths ? 2 : 1
  const secondMonthDate = new Date(currentDate.value)
  secondMonthDate.setMonth(secondMonthDate.getMonth() + monthsToCheck)
  secondMonthDate.setDate(0)

  return secondMonthDate <= maxDate
})

const getMonthData = offset => {
  const date = new Date(currentDate.value)
  date.setMonth(date.getMonth() + offset)
  return {
    year: date.getFullYear(),
    month: date.getMonth(),
  }
}

const getDaysInMonth = offset => {
  const { year, month } = getMonthData(offset)
  return new Date(year, month + 1, 0).getDate()
}

const getFirstDayOfMonth = offset => {
  const { year, month } = getMonthData(offset)
  const day = new Date(year, month, 1).getDay()
  return day === 0 ? 6 : day - 1
}

const getMonthYearDisplay = offset => {
  const { year, month } = getMonthData(offset)
  return { year, month }
}

const getDateTimestamp = (offset, day) => {
  const { year, month } = getMonthData(offset)
  const date = new Date(year, month, day)
  date.setHours(0, 0, 0, 0)
  return date.getTime()
}

const getDayTestId = (offset, day) => {
  const { year, month } = getMonthData(offset)
  const monthPart = String(month + 1).padStart(2, '0')
  const dayPart = String(day).padStart(2, '0')
  return `smart-boost-day-${year}-${monthPart}-${dayPart}`
}

const isToday = (offset, day) => {
  const today = new Date()
  const { year, month } = getMonthData(offset)
  return day === today.getDate() && month === today.getMonth() && year === today.getFullYear()
}

const isPastDate = (offset, day) => {
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const { year, month } = getMonthData(offset)
  const dateToCheck = new Date(year, month, day)
  return dateToCheck < today
}

const isSelected = (offset, day) => {
  const timestamp = getDateTimestamp(offset, day)

  return hasDate(timestamp)
}

const isTooFarInFuture = (offset, day) => {
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const maxDate = new Date(today)
  maxDate.setDate(maxDate.getDate() + 364)

  const { year, month } = getMonthData(offset)
  const dateToCheck = new Date(year, month, day)
  return dateToCheck > maxDate
}

const isDateSelectable = (offset, day) => {
  const timestamp = getDateTimestamp(offset, day)
  const hasActiveBoost = hasActiveBoostDate(timestamp)
  const alreadySelected = hasDate(timestamp)

  if (hasActiveBoost) return false
  if (props.hasReachedLimit && !alreadySelected) return false

  return !isPastDate(offset, day) && !isTooFarInFuture(offset, day)
}

const isDateSelectableByTimestamp = timestamp => {
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const maxDate = new Date(today)
  maxDate.setDate(maxDate.getDate() + 364)

  return timestamp >= today.getTime() && timestamp <= maxDate.getTime()
}

const getDayClasses = (offset, day) => {
  const tooFar = isTooFarInFuture(offset, day)
  const timestamp = getDateTimestamp(offset, day)
  const selected = isSelected(offset, day)
  const displayedBoost = hasActiveBoostDate(timestamp)
  const past = isPastDate(offset, day)
  const today = isToday(offset, day)

  if (past && displayedBoost) {
    return 'tw:font-bold tw:text-white tw:bg-gray-300 tw:cursor-not-allowed hover:tw:bg-gray-300'
  }

  if (past || tooFar) {
    return 'tw:text-gray-300 tw:cursor-not-allowed hover:tw:bg-transparent'
  }

  if (props.hasReachedLimit && !selected && !displayedBoost) {
    return 'tw:text-gray-300 tw:cursor-not-allowed hover:tw:bg-transparent'
  }

  if (displayedBoost && selected) {
    return 'tw:font-bold tw:border tw:border-2 tw:border-blue-700 hover:tw:bg-blue-200 tw:cursor-pointer'
  }

  if (displayedBoost && !props.isEditing) {
    return 'tw:font-bold tw:border tw:border-2 tw:border-blue-700  tw:cursor-not-allowed'
  }

  if (displayedBoost) {
    return 'tw:font-bold tw:border tw:border-2 tw:border-blue-700 hover:tw:bg-blue-200 tw:cursor-pointer'
  }

  if (selected) {
    if (isDragging.value && dragStart.value) {
      const isInDragRange = dragEnd.value
        ? timestamp >= Math.min(dragStart.value.timestamp, dragEnd.value.timestamp) &&
          timestamp <= Math.max(dragStart.value.timestamp, dragEnd.value.timestamp)
        : timestamp === dragStart.value.timestamp

      if (isInDragRange) {
        if (timestamp === dragStart.value.timestamp) {
          return 'tw:font-bold tw:bg-blue-700 tw:text-white hover:tw:bg-blue-700 tw:cursor-pointer'
        }
        return 'tw:font-bold tw:bg-blue-200 hover:tw:bg-blue-300 tw:cursor-pointer'
      }

      return 'tw:font-bold tw:bg-blue-700 tw:text-white hover:tw:bg-blue-700 tw:cursor-pointer'
    }
    return 'tw:font-bold tw:bg-blue-700 tw:text-white hover:tw:bg-blue-700 tw:cursor-pointer '
  }

  if (today) {
    return 'tw:font-bold tw:hover:bg-blue-200 tw:cursor-pointer'
  }

  return 'tw:text-gray-700 tw:hover:bg-blue-200 tw:cursor-pointer'
}

const handleMouseDown = (offset, day) => {
  if (!isDateSelectable(offset, day)) return

  const timestamp = getDateTimestamp(offset, day)
  const wasAlreadySelected = hasDate(timestamp)

  if (props.isEditing) {
    emit('switchToCreationMode')
  }

  setDragging(true)
  setDragStart({ offset, day, timestamp, wasAlreadySelected })
  setDragEnd(null)
  setHasMovedToAnotherDate(false)

  if (!wasAlreadySelected) {
    toggleDate(timestamp)
  }
}

const handleMouseEnter = (offset, day) => {
  if (!isDragging.value || !isDateSelectable(offset, day)) return
  const currentTimestamp = getDateTimestamp(offset, day)
  if (currentTimestamp === dragStart.value.timestamp) {
    return
  }

  setHasMovedToAnotherDate(true)
  setDragEnd({ offset, day, timestamp: currentTimestamp })

  const start = Math.min(dragStart.value.timestamp, dragEnd.value.timestamp)
  const end = Math.max(dragStart.value.timestamp, dragEnd.value.timestamp)

  let tempStart = new Date(start)
  while (!isDateSelectableByTimestamp(tempStart.getTime()) && tempStart <= new Date(end)) {
    tempStart.setDate(tempStart.getDate() + 1)
  }

  if (tempStart.getTime() <= end) {
    setRange(tempStart.getTime(), end)
  }
}

const handleMouseUp = () => {
  if (dragStart.value && !hasMovedToAnotherDate.value && dragStart.value.wasAlreadySelected) {
    toggleDate(dragStart.value.timestamp)
  }

  resetDragState()
  emit('selectionComplete')
}

const previousMonth = () => {
  if (!canGoToPreviousMonth.value) return
  const newDate = new Date(currentDate.value)
  newDate.setMonth(newDate.getMonth() - 1)
  currentDate.value = newDate
}

const nextMonth = () => {
  if (!canGoToNextMonth.value) return
  const newDate = new Date(currentDate.value)
  newDate.setMonth(newDate.getMonth() + 1)
  currentDate.value = newDate
}

const getDateAriaLabel = (offset, day) => {
  const { year, month } = getMonthData(offset)
  const date = new Date(year, month, day)
  const dateString = date.toLocaleDateString(undefined, {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  })

  const selected = isSelected(offset, day)
  const today = isToday(offset, day)
  const past = isPastDate(offset, day)
  const tooFar = isTooFarInFuture(offset, day)

  let label = dateString
  if (today) label += ', today'
  if (selected) label += ', selected'
  if (past) label += ', past date, not available'
  if (tooFar) label += ', too far in future, not available'

  return label
}

const handleKeyboardSelect = (offset, day) => {
  if (!isDateSelectable(offset, day)) return
  const timestamp = getDateTimestamp(offset, day)

  if (props.isEditing) {
    emit('switchToCreationMode')
  }

  toggleDate(timestamp)
}
</script>
